import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import { getRecommendationDetails, getRecommendations } from 'apis/RecommendationsAPI';
import useRecommendationsContext from 'components/AiCompetitiveIntelligence/RecommendationsContext';
import RecommendationsNotFound from 'components/AiCompetitiveIntelligence/RecommendationsNotFound';
import ErrorMessage from 'components/lib/ErrorMessage';
import Loader from 'components/lib/Loader';
import { useAppSelector } from 'store/hook';

import { SPEED_TO_LEAD_CONFIG_ID, SPEED_TO_LEAD_QUERY_KEY } from './constants';
import SpeedToLeadOverall from './SpeedToLeadOverall';

const SpeedToLeadLatestRoot = () => {
  const { company } = useRecommendationsContext();
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const communityId = selectedCommunity?.id;
  const {
    data: recommendations = [],
    isError: recommendationsError,
    isLoading: recommendationsLoading,
  } = useQuery({
    queryKey: [SPEED_TO_LEAD_QUERY_KEY.RECOMMENDATIONS, company?.id, communityId],
    queryFn: () =>
      getRecommendations(company?.id ?? -1, {
        communities: communityId ? [communityId] : [],
        speed_to_lead: true,
      }),
    enabled: Boolean(company?.id && communityId),
    refetchOnWindowFocus: false,
  });
  const recommendationId = useMemo(
    () => recommendations.find((item) => item.config.id === SPEED_TO_LEAD_CONFIG_ID.OVERALL)?.id,
    [recommendations]
  );
  const {
    data: recommendation,
    isError: detailsError,
    isLoading: detailsLoading,
  } = useQuery({
    queryKey: [SPEED_TO_LEAD_QUERY_KEY.DETAILS, recommendationId],
    queryFn: () => getRecommendationDetails(recommendationId ?? -1),
    enabled: Boolean(recommendationId),
    refetchOnWindowFocus: false,
  });

  if (recommendationsLoading || detailsLoading || !communityId) {
    return <Loader active />;
  }

  if (recommendationsError || detailsError) {
    return <ErrorMessage text="Failed to load the latest Speed to Lead insight. Please try again." active />;
  }

  if (!recommendationId || !recommendation) {
    return (
      <RecommendationsNotFound
        title="There is no Speed to Lead insight for this community yet."
        description="Ask an administrator to generate a Speed to Lead analysis for this community."
      />
    );
  }

  return <SpeedToLeadOverall recommendation={recommendation} />;
};

export default SpeedToLeadLatestRoot;
